import { TypedDocumentNode, gql } from '@apollo/client';
import { generatePath } from 'react-router-dom';

import { PlayerInFixtureStatusIconType } from '@sorare/core/src/__generated__/globalTypes';
import { Text16 } from '@sorare/core/src/atoms/typography';
import { NBA_TEAM } from '@sorare/core/src/constants/routes';

import { PlayerFixtureScoreState } from '@sorare/us-sports/src/components/player/fixtureStats/scoreState';
import {
  PlayerAvatar,
  PlayerBlock,
  PlayerNameAndTeam,
  PlayerTeamLink,
  Wrapper,
} from '@sorare/us-sports/src/components/stats/DialogBody';

import GameStatsCard from '../GameStatsCard';
import {
  NBALastGamesPerformanceDialogBody_gameStats,
  NBALastGamesPerformanceDialogBody_player,
} from './__generated__/index.graphql';

type Props = {
  player: NBALastGamesPerformanceDialogBody_player;
  gameStats: NBALastGamesPerformanceDialogBody_gameStats;
};

export const LastGamesPerformanceDialogBody = ({
  player,
  gameStats,
}: Props) => {
  const gameScore = gameStats.score;
  return (
    <Wrapper>
      <PlayerBlock>
        <PlayerAvatar player={player} />
        <PlayerNameAndTeam>
          <Text16 bold>{player.displayName}</Text16>
          {player.activeClub && (
            <PlayerTeamLink
              to={generatePath(NBA_TEAM, { slug: player.activeClub.slug })}
            >
              {player.activeClub.name}
            </PlayerTeamLink>
          )}
        </PlayerNameAndTeam>
        <PlayerFixtureScoreState
          score={gameScore}
          iconType={PlayerInFixtureStatusIconType.FINAL_SCORE}
        />
      </PlayerBlock>

      <GameStatsCard
        key={gameStats.game.id}
        game={gameStats.game}
        detailedStats={gameStats.detailedStats}
        detailedScores={gameStats.detailedScores}
        score={gameScore}
        bonus={0}
        showBonus={false}
      />
    </Wrapper>
  );
};

LastGamesPerformanceDialogBody.fragments = {
  player: gql`
    fragment NBALastGamesPerformanceDialogBody_player on NBAPlayer {
      slug
      displayName
      avatarPictureUrl
      activeClub {
        slug
        name
      }
    }
  ` as TypedDocumentNode<NBALastGamesPerformanceDialogBody_player>,
  gameStats: gql`
    fragment NBALastGamesPerformanceDialogBody_gameStats on NBAPlayerGameStats {
      score
      game {
        id
        ...GameStatsCard_nbaGame
      }
      ...NBAGameStatsCard_gameStats
    }
    ${GameStatsCard.fragments.gameStats}
    ${GameStatsCard.fragments.nbaGame}
  ` as TypedDocumentNode<NBALastGamesPerformanceDialogBody_gameStats>,
};
